import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Eye,
  Heart,
  Star,
  MessageCircle,
  Sparkles,
  Pencil,
  Archive,
  ChevronDown,
  Mail,
  HelpCircle,
  Calendar,
  UserCircle,
  MapPin,
} from "lucide-react";
import QuietMarketingLayout, {
  QuietPrimaryButton,
  QuietSecondaryButton,
} from "../components/QuietMarketingLayout";

const SECTIONS = [
  { id: "getting-started", label: "RSVP" },
  { id: "privacy", label: "Privacy" },
  { id: "profile", label: "Profile" },
  { id: "hosting", label: "Hosting" },
  { id: "community-games", label: "Games" },
  { id: "coming-soon", label: "Roadmap" },
];

function FaqAccordion({ question, children, icon: Icon, defaultOpen = false }) {
  const testId = `faq-item-${question.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;

  return (
    <div
      className="rounded-2xl border border-black/[0.04] bg-white overflow-hidden"
      data-testid={testId}
    >
      <details className="group" open={defaultOpen}>
        <summary className="list-none [&::-webkit-details-marker]:hidden cursor-pointer p-5 sm:p-6 focus-visible:outline-offset-[-2px]">
          <div className="flex items-start gap-3">
            {Icon ? (
              <span className="shrink-0 mt-0.5 text-ink/40" aria-hidden>
                <Icon className="w-4 h-4" strokeWidth={2} />
              </span>
            ) : null}
            <div className="min-w-0 flex-1 flex items-start justify-between gap-3">
              <h3 className="font-ui font-semibold text-[16px] sm:text-[17px] leading-snug text-left text-ink">
                {question}
              </h3>
              <ChevronDown
                className="w-5 h-5 shrink-0 mt-0.5 text-ink/40 transition-transform motion-reduce:transition-none group-open:rotate-180"
                strokeWidth={2}
                aria-hidden
              />
            </div>
          </div>
        </summary>
        <div className="px-5 pb-5 sm:px-6 sm:pb-6 pt-0 border-t border-black/[0.04]">
          <div className="pt-4 sm:pl-7 space-y-3 text-[15px] font-normal text-ink/70 leading-relaxed">
            {children}
          </div>
        </div>
      </details>
    </div>
  );
}

function FaqSection({ id, title, children }) {
  return (
    <section id={id} className="pb-10 scroll-mt-24">
      <h2 className="font-display font-semibold text-[22px] sm:text-[24px] leading-tight mb-4 text-ink">
        {title}
      </h2>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function ComingSoonItem({ question, children, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-black/[0.04] bg-white p-5 sm:p-6">
      <div className="flex items-start gap-3">
        {Icon ? (
          <span className="shrink-0 mt-0.5 text-ink/40" aria-hidden>
            <Icon className="w-4 h-4" strokeWidth={2} />
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <h3 className="font-ui font-semibold text-[16px] sm:text-[17px] leading-snug text-ink">
            {question}
          </h3>
          <p className="mt-1.5 font-ui text-[12px] font-medium text-ink/50">Coming soon</p>
          <div className="mt-3 space-y-3 text-[15px] font-normal text-ink/70 leading-relaxed">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQPage() {
  useEffect(() => {
    document.title = "FAQ — rsvp.dance";
    return () => {
      document.title = "Find your next social.";
    };
  }, []);

  return (
    <QuietMarketingLayout
      title="FAQ"
      description="Everything you need to know about RSVPing, hosting, and privacy for dance socials."
      backTestId="faq-back-link"
      brandTestId="faq-brand-link"
      heroExtra={
        <nav
          className="mt-8 flex flex-wrap gap-x-4 gap-y-2"
          aria-label="Jump to FAQ section"
          data-testid="faq-section-nav"
        >
          {SECTIONS.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              className="font-ui font-medium text-[13px] text-cream/65 hover:text-cream underline underline-offset-4 decoration-white/20 hover:decoration-white/45 transition-colors min-h-[44px] inline-flex items-center"
            >
              {label}
            </a>
          ))}
        </nav>
      }
    >
      <FaqSection id="getting-started" title="Getting started">
        <FaqAccordion question="Do I need an account to RSVP?" icon={UserCircle} defaultOpen>
          <p>
            No account needed. Tap your event link, pick your dance role, and you are on the list. You can create an
            account later to manage events or host socials.
          </p>
        </FaqAccordion>

        <FaqAccordion question="How do I RSVP from a WhatsApp link?" icon={Calendar}>
          <p>
            Open the link, tap <strong className="font-semibold text-ink">RSVP</strong>, enter your name, and select
            your role. You will appear on the live guest list instantly.
          </p>
        </FaqAccordion>

        <FaqAccordion question="How do I find events near me?" icon={MapPin}>
          <p>
            Select your city on the{" "}
            <Link to="/#events" className="text-ink underline underline-offset-2 font-medium">
              home page
            </Link>{" "}
            to filter upcoming socials. Logged-in users can view their saved socials on the{" "}
            <Link to="/events" className="text-ink underline underline-offset-2 font-medium">
              My Events
            </Link>{" "}
            page.
          </p>
        </FaqAccordion>

        <FaqAccordion question="Can I change or cancel my RSVP?" icon={Pencil}>
          <p>
            Reopen your original event link or manage it under{" "}
            <strong className="font-semibold text-ink">My Events</strong>. You can update or cancel your reservation
            anytime before the event starts.
          </p>
        </FaqAccordion>
      </FaqSection>

      <FaqSection id="privacy" title="Privacy & your data">
        <FaqAccordion question="Is the guest list public?" icon={Shield}>
          <p>
            <strong className="font-semibold text-ink">No.</strong> Guest lists are strictly private. You can only view
            attendees if you have the event link and have submitted an RSVP for that specific night.
          </p>
        </FaqAccordion>

        <FaqAccordion question="Can other guests see my contact details?" icon={Eye}>
          <p>
            <strong className="font-semibold text-ink">No.</strong> Contact information is never shared. Other attendees
            can only see your name, dance role, and profile photo.
          </p>
        </FaqAccordion>
      </FaqSection>

      <FaqSection id="profile" title="Your profile">
        <FaqAccordion question="Why is relationship status requested?" icon={Heart}>
          <p>
            This optional ice-breaker helps people connect at events. You can select single, in a relationship, or skip
            the question entirely.
          </p>
        </FaqAccordion>
      </FaqSection>

      <FaqSection id="hosting" title="Hosting your own social">
        <FaqAccordion question="Can I save a draft before publishing?" icon={Pencil}>
          <p>
            <strong className="font-semibold text-ink">Yes.</strong> Tap{" "}
            <strong className="font-semibold text-ink">Save Draft</strong> at any point while creating an event. Your
            drafts remain private under <strong className="font-semibold text-ink">Profile → Host → Events</strong> until
            you hit <strong className="font-semibold text-ink">Publish</strong>.
          </p>
        </FaqAccordion>

        <FaqAccordion question="How do I archive or delete an event?" icon={Archive}>
          <p>
            Go to your Event <strong className="font-semibold text-ink">Dashboard</strong> Overview tab.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="font-semibold text-ink">Archive:</strong> Hides the event page and pauses RSVPs. You
              can restore archived events later.
            </li>
            <li>
              <strong className="font-semibold text-ink">Delete:</strong> Permanently removes events that have zero
              RSVPs. If attendees have already signed up, use Archive instead.
            </li>
          </ul>
        </FaqAccordion>
      </FaqSection>

      <FaqSection id="community-games" title="Favorite Dances">
        <FaqAccordion question="How does Favorite Dances voting work?" icon={Star}>
          <p>
            After check-in, vote for up to three favorite dance partners on the event page or under{" "}
            <strong className="font-semibold text-ink">My Events</strong>. Votes are completely anonymous and help
            recommend future socials where your favorite partners are attending.
          </p>
        </FaqAccordion>
      </FaqSection>

      <section id="coming-soon" className="pb-10 scroll-mt-24">
        <details className="group">
          <summary className="list-none [&::-webkit-details-marker]:hidden cursor-pointer">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-ink/40" strokeWidth={2} aria-hidden />
              <h2 className="font-display font-semibold text-[22px] sm:text-[24px] leading-tight text-ink">
                On the roadmap
              </h2>
              <ChevronDown
                className="w-5 h-5 ml-auto text-ink/40 transition-transform motion-reduce:transition-none group-open:rotate-180"
                strokeWidth={2}
                aria-hidden
              />
            </div>
            <p className="font-normal text-[15px] text-ink/55 mb-4 max-w-lg text-pretty">
              Tap to see community features we&apos;re still building — student testimonials for teachers.
            </p>
          </summary>

          <div className="space-y-2.5 mt-2">
            <ComingSoonItem question="Teacher testimonials" icon={MessageCircle}>
              <p>
                Students will be able to leave <strong className="font-semibold text-ink">testimonials for teachers</strong>{" "}
                — a genuine note from someone who took your class or workshop beats a random compliment any day.
              </p>
            </ComingSoonItem>
          </div>
        </details>
      </section>

      <section className="rounded-2xl border border-black/[0.04] bg-white p-5 sm:p-6 mb-12">
        <div className="flex items-start gap-3">
          <HelpCircle className="w-4 h-4 shrink-0 mt-1 text-ink/40" strokeWidth={2} aria-hidden />
          <div className="min-w-0">
            <h2 className="font-ui font-semibold text-[17px] sm:text-[18px] leading-snug text-ink">Still stuck?</h2>
            <p className="mt-2 text-[15px] font-normal text-ink/65 leading-relaxed text-pretty">
              Didn&apos;t find your answer? Reach out — we&apos;re a small team and we read every message.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <QuietSecondaryButton as={Link} to="/support" data-testid="faq-support-link">
                Contact support
              </QuietSecondaryButton>
              <QuietSecondaryButton
                as="a"
                href="mailto:flipflopapps@gmail.com"
                data-testid="faq-email-link"
              >
                <Mail className="w-4 h-4" strokeWidth={2} aria-hidden />
                Email us
              </QuietSecondaryButton>
            </div>
          </div>
        </div>
      </section>

      <section className="pt-2 pb-4 text-center sm:text-left">
        <p className="font-display font-semibold text-[18px] sm:text-[20px] text-ink text-balance">
          Find your next dance night.
        </p>
        <p className="mt-1.5 text-[14px] font-normal text-ink/55">
          Simple RSVPs for social dancers and community hosts.
        </p>
        <div className="mt-5">
          <QuietPrimaryButton as={Link} to="/#events" data-testid="faq-browse-events-btn">
            Explore Events
          </QuietPrimaryButton>
        </div>
      </section>
    </QuietMarketingLayout>
  );
}
